import React, { useState } from "react";
import QRCode from "qrcode";
import Modal from "../../../components/Shared/Modal";
import ConfirmDialog from "../../../components/Shared/ConfirmDialog";
import { CognitoAuthService } from "../../../services/cognitoService";
import { useToast } from "../../../context/ToastContext";
import useMfaStatus from "../hooks/useMfaStatus";
import { debug, formatSafeErrorMessage } from "../../../utils/debugLogger";

const labelClass =
  "text-xs font-semibold uppercase tracking-wide text-zinc-500";

interface MfaSettingsProps {
  idToken: string | null;
}

const MfaSettings: React.FC<MfaSettingsProps> = ({ idToken }) => {
  const { showToast } = useToast();
  const { enabled, loading, error, refetch } = useMfaStatus();

  const [enrollOpen, setEnrollOpen] = useState(false);
  const [secret, setSecret] = useState("");
  const [qrDataUrl, setQrDataUrl] = useState("");
  const [code, setCode] = useState("");
  const [enrollError, setEnrollError] = useState("");
  const [starting, setStarting] = useState(false);
  const [verifying, setVerifying] = useState(false);

  const [disableOpen, setDisableOpen] = useState(false);
  const [disabling, setDisabling] = useState(false);
  const [disableError, setDisableError] = useState<string | null>(null);

  const startEnrollment = async () => {
    setStarting(true);
    try {
      const secretCode = await CognitoAuthService.associateSoftwareToken();
      const email = CognitoAuthService.getUserEmail(idToken) || "account";
      const otpauthUri = `otpauth://totp/Sagelyn:${encodeURIComponent(
        email
      )}?secret=${secretCode}&issuer=Sagelyn&algorithm=SHA1&digits=6&period=30`;
      const dataUrl = await QRCode.toDataURL(otpauthUri);
      setSecret(secretCode);
      setQrDataUrl(dataUrl);
      setEnrollError("");
      setCode("");
      setEnrollOpen(true);
    } catch (err: any) {
      debug.error("Failed to start MFA enrollment:", formatSafeErrorMessage(err));
      showToast(
        err.message || "Couldn't start two-factor setup. Please try again.",
        "error"
      );
    } finally {
      setStarting(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setEnrollError("");
    setVerifying(true);
    try {
      await CognitoAuthService.verifySoftwareToken(code, "Authenticator app");
      setEnrollOpen(false);
      setCode("");
      showToast("Two-factor authentication enabled", "success");
      refetch();
    } catch (err: any) {
      setEnrollError(err.message || "Invalid code. Please try again.");
    } finally {
      setVerifying(false);
    }
  };

  const closeEnrollModal = () => {
    setEnrollOpen(false);
    setCode("");
    setEnrollError("");
  };

  const handleDisable = async () => {
    setDisabling(true);
    setDisableError(null);
    try {
      await CognitoAuthService.disableMfa();
      setDisableOpen(false);
      showToast("Two-factor authentication disabled", "success");
      refetch();
    } catch (err: any) {
      setDisableError(err.message || "Couldn't disable two-factor authentication.");
    } finally {
      setDisabling(false);
    }
  };

  return (
    <div className="space-y-3">
      <p className="text-xs font-semibold uppercase tracking-wide text-zinc-500 dark:text-theme-fg-muted">
        Two-factor authentication
      </p>
      <div className="flex flex-col gap-4 rounded-lg border border-zinc-200/90 dark:border-theme-border bg-zinc-50/50 dark:bg-theme-surface p-6 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex min-w-0 items-start gap-4 sm:items-center">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl border border-zinc-200/80 dark:border-theme-border bg-white dark:bg-theme-background text-brand-secondary">
            <span className="material-icons-outlined text-3xl">
              verified_user
            </span>
          </div>
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-zinc-900 dark:text-theme-fg">
              {loading ? "Checking status…" : enabled ? "Enabled" : "Not enabled"}
            </h3>
            <p className="mt-0.5 text-sm text-zinc-600 dark:text-theme-fg-muted">
              {enabled
                ? "Your account is protected with an authenticator app code at sign-in."
                : "Add an authenticator app code as a second step at sign-in."}
            </p>
            {error ? <p className="mt-1 text-xs text-red-500 dark:text-red-400">{error}</p> : null}
          </div>
        </div>
        {!loading && !enabled ? (
          <button
            type="button"
            onClick={startEnrollment}
            disabled={starting}
            className="rounded-lg bg-brand-secondary px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-colors hover:opacity-90 disabled:opacity-50"
          >
            {starting ? "Starting…" : "Enable two-factor authentication"}
          </button>
        ) : null}
        {!loading && enabled ? (
          <button
            type="button"
            onClick={() => setDisableOpen(true)}
            className="rounded-lg border border-zinc-200 dark:border-theme-border bg-white dark:bg-theme-background px-4 py-2.5 text-sm font-medium text-zinc-700 dark:text-theme-fg transition-colors hover:bg-zinc-50 dark:hover:bg-zinc-800"
          >
            Disable
          </button>
        ) : null}
      </div>

      {enrollOpen && (
        <Modal onClose={closeEnrollModal} title="Set up two-factor authentication" size="sm">
          <div className="space-y-4">
            <p className="text-sm text-zinc-600">
              Scan this QR code with your authenticator app (e.g. Google
              Authenticator, Authy), then enter the 6-digit code it shows.
            </p>
            {qrDataUrl ? (
              <img
                src={qrDataUrl}
                alt="Scan with your authenticator app"
                className="mx-auto h-48 w-48"
              />
            ) : null}
            <div>
              <p className={labelClass}>Can&apos;t scan? Enter this code manually</p>
              <p className="mt-1 break-all rounded-lg border border-zinc-200/90 bg-zinc-50 p-3 text-sm font-mono text-zinc-700">
                {secret}
              </p>
            </div>
            <form onSubmit={handleVerify} className="space-y-4">
              {enrollError && (
                <div className="rounded-lg border border-red-200 bg-red-50 p-3">
                  <p className="text-sm text-red-800">{enrollError}</p>
                </div>
              )}
              <div>
                <label htmlFor="mfa-code" className={labelClass}>
                  6-digit code
                </label>
                <input
                  id="mfa-code"
                  name="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={6}
                  required
                  className="mt-1.5 block w-full rounded-lg border border-zinc-200/90 bg-white px-4 py-3 text-base text-zinc-900 shadow-sm focus:border-brand-primary-light focus:outline-none focus:ring-2 focus:ring-brand-secondary/35"
                  value={code}
                  onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                />
              </div>
              <button
                type="submit"
                disabled={verifying || code.length !== 6}
                className="flex w-full justify-center rounded-lg bg-brand-primary px-4 py-3 text-sm font-semibold text-white transition-colors hover:bg-brand-primary-hover disabled:opacity-60"
              >
                {verifying ? "Verifying…" : "Verify and enable"}
              </button>
            </form>
          </div>
        </Modal>
      )}

      <ConfirmDialog
        isOpen={disableOpen}
        title="Disable two-factor authentication?"
        message="You'll no longer be asked for an authenticator app code at sign-in."
        confirmLabel="Disable"
        isConfirming={disabling}
        destructive
        errorMessage={disableError}
        onConfirm={handleDisable}
        onCancel={() => {
          if (!disabling) {
            setDisableOpen(false);
            setDisableError(null);
          }
        }}
      />
    </div>
  );
};

export default MfaSettings;
